/**
 * Canvas authoring tools.
 *
 * The live authoring operations proxy Microsoft's preview Canvas authoring MCP.
 * The local source operations deliberately expose only the flat `.pa.yaml`
 * workspace used by that server. They do not provide a general-purpose file API.
 */

import { createHash, randomUUID } from "node:crypto";
import { constants as fsConstants } from "node:fs";
import { access, lstat, open, readdir, realpath, rename, unlink } from "node:fs/promises";
import { homedir } from "node:os";
import { basename, dirname, isAbsolute, join, parse as parsePath, resolve } from "node:path";
import { TextDecoder } from "node:util";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import { z } from "zod";
import type { CanvasAuthoringClientLike } from "../api/canvas-authoring-client.js";
import { ApiError, ValidationError, ConfirmationRequiredError } from "../utils/errors.js";

const MAX_CANVAS_SOURCE_BYTES = 1024 * 1024;
const MAX_CANVAS_SOURCE_FILES = 256;
const MAX_CANVAS_WORKSPACE_BYTES = 32 * 1024 * 1024;
const SHA256_PATTERN = /^[0-9a-f]{64}$/i;
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ENVIRONMENT_ID_PATTERN = new RegExp(
  `^(?:Default-)?${GUID_PATTERN.source.slice(1, -1)}$`,
  "i",
);
const CANVAS_SOURCE_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._ -]{0,239}\.pa\.yaml$/;

const ENVIRONMENT_CATEGORIES = ["test", "preprod", "prod", "gov", "high", "dod", "china"] as const;

type EnvironmentCategory = (typeof ENVIRONMENT_CATEGORIES)[number];

const TRUSTED_STUDIO_HOSTS: Readonly<Record<string, EnvironmentCategory>> = {
  "make.powerapps.com": "prod",
  "make.preview.powerapps.com": "prod",
  "make.gov.powerapps.us": "gov",
  "make.high.powerapps.us": "high",
  "make.apps.appsplatform.us": "dod",
  "make.powerapps.cn": "china",
};

const guidSchema = z.string().regex(GUID_PATTERN, "Expected a GUID");
const environmentIdSchema = z
  .string()
  .regex(
    ENVIRONMENT_ID_PATTERN,
    "Expected a Power Platform environment ID (GUID or Default-<GUID>)",
  );
const sourceNameSchema = z.string().superRefine((value, issue) => {
  try {
    validateCanvasSourceName(value);
  } catch (error) {
    issue.addIssue({
      code: "custom",
      message: error instanceof Error ? error.message : "Invalid Canvas source file name",
    });
  }
});
const expectedSha256Schema = z
  .string()
  .regex(SHA256_PATTERN, "expectedSha256 must be a 64-character hexadecimal SHA-256 digest")
  .optional();

const connectSchema = z
  .object({
    studioUrl: z.url().optional(),
    environmentId: environmentIdSchema.optional(),
    appId: guidSchema.optional(),
    environmentCategory: z.enum(ENVIRONMENT_CATEGORIES).optional(),
    authFlow: z.enum(["broker", "browser", "devicecode"]).optional(),
    loginHint: z.string().trim().min(1).max(320).optional(),
    tenantId: guidSchema.optional(),
    forceAccountSelect: z.boolean().optional(),
  })
  .strict()
  .superRefine((value, issue) => {
    const hasExplicit =
      value.environmentId !== undefined ||
      value.appId !== undefined ||
      value.environmentCategory !== undefined;
    if (value.studioUrl && hasExplicit) {
      issue.addIssue({
        code: "custom",
        message:
          "Use either studioUrl or environmentId, appId, and environmentCategory; do not mix them",
      });
      return;
    }
    if (!value.studioUrl) {
      if (!value.environmentId || !value.appId || !value.environmentCategory) {
        issue.addIssue({
          code: "custom",
          message:
            "Provide a trusted studioUrl or all of environmentId, appId, and environmentCategory",
        });
      }
    }
  });

const emptySchema = z.object({}).strict();
const describeControlSchema = z.object({ controlName: z.string().trim().min(1).max(256) }).strict();
const describeApiSchema = z.object({ apiName: z.string().trim().min(1).max(512) }).strict();
const dataSourceSchema = z.object({ dataSourceName: z.string().trim().min(1).max(512) }).strict();
const directorySchema = z.object({ directoryPath: z.string().min(1) }).strict();
const syncSchema = z
  .object({ directoryPath: z.string().min(1), confirmOverwrite: z.boolean().optional() })
  .strict();
const compileSchema = z
  .object({ directoryPath: z.string().min(1), confirm: z.boolean().optional() })
  .strict();
const fileSchema = z
  .object({ directoryPath: z.string().min(1), fileName: sourceNameSchema })
  .strict();
const writeSchema = z
  .object({
    directoryPath: z.string().min(1),
    fileName: sourceNameSchema,
    content: z.string(),
    overwrite: z.boolean().optional(),
    expectedSha256: expectedSha256Schema,
  })
  .strict()
  .superRefine((value, issue) => {
    if (value.expectedSha256 && !value.overwrite) {
      issue.addIssue({
        code: "custom",
        message: "expectedSha256 is only valid when overwrite=true",
      });
    }
  });
const deleteSchema = z
  .object({
    directoryPath: z.string().min(1),
    fileName: sourceNameSchema,
    confirm: z.boolean().optional(),
    expectedSha256: expectedSha256Schema,
  })
  .strict();

const guidProperty = {
  type: "string",
  pattern: GUID_PATTERN.source,
} as const;
const environmentIdProperty = {
  type: "string",
  pattern: ENVIRONMENT_ID_PATTERN.source,
  description: "Power Platform environment ID (GUID or Default-<GUID>)",
} as const;
const directoryPathProperty = {
  type: "string",
  description:
    "Existing absolute, normalized local directory containing the flat Canvas .pa.yaml workspace",
} as const;
const fileNameProperty = {
  type: "string",
  pattern: CANVAS_SOURCE_NAME_PATTERN.source,
  maxLength: 248,
  description:
    "Flat Canvas source basename ending in .pa.yaml (for example App.pa.yaml or Screen1.pa.yaml); path separators are forbidden",
} as const;
const expectedSha256Property = {
  type: "string",
  pattern: SHA256_PATTERN.source,
  description:
    "Optional SHA-256 returned by a prior read/list. The mutation fails if the current file differs.",
} as const;

export const connectCanvasAuthoringTool: Tool = {
  name: "connect_canvas_authoring",
  description:
    "Connect the preview Canvas authoring service to an existing app whose Power Apps Studio tab is open with coauthoring enabled. Accepts either a trusted Designer URL or explicit app/environment IDs and environment category. Device-code auth is not supported through this bridge.",
  inputSchema: {
    type: "object",
    properties: {
      studioUrl: {
        type: "string",
        format: "uri",
        description:
          "HTTPS Power Apps Designer URL on a trusted Microsoft maker host. Mutually exclusive with explicit IDs/category.",
      },
      environmentId: environmentIdProperty,
      appId: { ...guidProperty, description: "Canvas app GUID" },
      environmentCategory: {
        type: "string",
        enum: [...ENVIRONMENT_CATEGORIES],
        description: "Microsoft environment cluster category",
      },
      authFlow: {
        type: "string",
        enum: ["broker", "browser"],
        description: "Interactive authentication flow (devicecode is intentionally unsupported)",
      },
      loginHint: { type: "string", maxLength: 320 },
      tenantId: { ...guidProperty, description: "Optional Entra tenant GUID" },
      forceAccountSelect: { type: "boolean" },
    },
    oneOf: [
      { required: ["studioUrl"] },
      { required: ["environmentId", "appId", "environmentCategory"] },
    ],
    additionalProperties: false,
  },
};

export const listCanvasControlsTool: Tool = {
  name: "list_canvas_controls",
  description: "List controls supported by the connected Canvas authoring session.",
  inputSchema: { type: "object", properties: {}, additionalProperties: false },
};

export const describeCanvasControlTool: Tool = {
  name: "describe_canvas_control",
  description: "Describe one Canvas control and its authoring properties.",
  inputSchema: {
    type: "object",
    properties: {
      controlName: { type: "string", minLength: 1, maxLength: 256 },
    },
    required: ["controlName"],
    additionalProperties: false,
  },
};

export const listCanvasApisTool: Tool = {
  name: "list_canvas_apis",
  description: "List connector APIs visible to the connected Canvas authoring session.",
  inputSchema: { type: "object", properties: {}, additionalProperties: false },
};

export const describeCanvasApiTool: Tool = {
  name: "describe_canvas_api",
  description: "Describe one connector API visible to the connected Canvas app.",
  inputSchema: {
    type: "object",
    properties: { apiName: { type: "string", minLength: 1, maxLength: 512 } },
    required: ["apiName"],
    additionalProperties: false,
  },
};

export const listCanvasDataSourcesTool: Tool = {
  name: "list_canvas_data_sources",
  description: "List data sources already present in the connected Canvas app.",
  inputSchema: { type: "object", properties: {}, additionalProperties: false },
};

export const getCanvasDataSourceSchemaTool: Tool = {
  name: "get_canvas_data_source_schema",
  description: "Get the schema of a data source already present in the connected Canvas app.",
  inputSchema: {
    type: "object",
    properties: { dataSourceName: { type: "string", minLength: 1, maxLength: 512 } },
    required: ["dataSourceName"],
    additionalProperties: false,
  },
};

export const syncCanvasSourceTool: Tool = {
  name: "sync_canvas_source",
  description:
    "Sync the connected live Canvas app into an existing local source directory. This can overwrite local files and requires confirmOverwrite=true.",
  inputSchema: {
    type: "object",
    properties: {
      directoryPath: directoryPathProperty,
      confirmOverwrite: {
        type: "boolean",
        description: "Set true to permit the authoring server to overwrite local source files",
      },
    },
    required: ["directoryPath"],
    additionalProperties: false,
  },
};

export const compileCanvasSourceTool: Tool = {
  name: "compile_canvas_source",
  description:
    "Compile the local .pa.yaml workspace into the connected live Canvas app. This is a destructive tenant write even when validation fails and requires confirm=true.",
  inputSchema: {
    type: "object",
    properties: {
      directoryPath: directoryPathProperty,
      confirm: {
        type: "boolean",
        description: "Set true to confirm modifying the connected live Canvas app",
      },
    },
    required: ["directoryPath"],
    additionalProperties: false,
  },
};

export const listCanvasSourceFilesTool: Tool = {
  name: "list_canvas_source_files",
  description:
    "List safe .pa.yaml files in an existing local Canvas source directory, including size and SHA-256.",
  inputSchema: {
    type: "object",
    properties: { directoryPath: directoryPathProperty },
    required: ["directoryPath"],
    additionalProperties: false,
  },
};

export const readCanvasSourceFileTool: Tool = {
  name: "read_canvas_source_file",
  description:
    "Read one safe UTF-8 .pa.yaml file (maximum 1 MiB) and return its content, size, and SHA-256.",
  inputSchema: {
    type: "object",
    properties: { directoryPath: directoryPathProperty, fileName: fileNameProperty },
    required: ["directoryPath", "fileName"],
    additionalProperties: false,
  },
};

export const writeCanvasSourceFileTool: Tool = {
  name: "write_canvas_source_file",
  description:
    "Create one safe UTF-8 .pa.yaml file (maximum 1 MiB). Existing files are preserved unless overwrite=true; expectedSha256 can prevent lost updates.",
  inputSchema: {
    type: "object",
    properties: {
      directoryPath: directoryPathProperty,
      fileName: fileNameProperty,
      content: { type: "string", maxLength: MAX_CANVAS_SOURCE_BYTES },
      overwrite: { type: "boolean", description: "Set true to overwrite an existing file" },
      expectedSha256: expectedSha256Property,
    },
    required: ["directoryPath", "fileName", "content"],
    additionalProperties: false,
  },
};

export const deleteCanvasSourceFileTool: Tool = {
  name: "delete_canvas_source_file",
  description:
    "Delete one safe local .pa.yaml file. Requires confirm=true; expectedSha256 can prevent deleting a changed file.",
  inputSchema: {
    type: "object",
    properties: {
      directoryPath: directoryPathProperty,
      fileName: fileNameProperty,
      confirm: { type: "boolean", description: "Set true to confirm deletion" },
      expectedSha256: expectedSha256Property,
    },
    required: ["directoryPath", "fileName"],
    additionalProperties: false,
  },
};

export const canvasAuthoringTools: Tool[] = [
  connectCanvasAuthoringTool,
  listCanvasControlsTool,
  describeCanvasControlTool,
  listCanvasApisTool,
  describeCanvasApiTool,
  listCanvasDataSourcesTool,
  getCanvasDataSourceSchemaTool,
  syncCanvasSourceTool,
  compileCanvasSourceTool,
  listCanvasSourceFilesTool,
  readCanvasSourceFileTool,
  writeCanvasSourceFileTool,
  deleteCanvasSourceFileTool,
];

export interface CanvasAuthoringToolContext {
  client: CanvasAuthoringClientLike | null;
}

export interface CanvasAuthoringRichResult {
  text: string;
  structured?: Record<string, unknown>;
}

export type CanvasAuthoringHandlerResult = string | CanvasAuthoringRichResult;

interface CanvasSourceFile {
  fileName: string;
  sizeBytes: number;
  sha256: string;
  content: string;
  device: number;
  inode: number;
}

function requireCanvasClient(context: CanvasAuthoringToolContext): CanvasAuthoringClientLike {
  if (!context.client) {
    throw new ValidationError(
      "Canvas authoring MCP is not configured. Install .NET 10 and enable the Canvas authoring integration before calling this tool.",
    );
  }
  return context.client;
}

function parseTrustedStudioUrl(studioUrl: string): {
  environmentId: string;
  appId: string;
  environmentCategory: EnvironmentCategory;
} {
  let parsed: URL;
  try {
    parsed = new URL(studioUrl);
  } catch {
    throw new ApiError("studioUrl must be a valid absolute HTTPS URL", 400);
  }

  if (
    parsed.protocol !== "https:" ||
    parsed.username !== "" ||
    parsed.password !== "" ||
    parsed.port !== ""
  ) {
    throw new ApiError(
      "studioUrl must use HTTPS on a trusted Microsoft maker host without credentials or a custom port",
      400,
    );
  }
  const environmentCategory = TRUSTED_STUDIO_HOSTS[parsed.hostname.toLowerCase()];
  if (!environmentCategory) {
    throw new ApiError("studioUrl host is not a trusted Microsoft Power Apps Studio host", 400);
  }

  let segments: string[];
  try {
    segments = parsed.pathname
      .split("/")
      .filter(Boolean)
      .map((segment) => decodeURIComponent(segment));
  } catch {
    throw new ApiError("studioUrl path contains invalid URL encoding", 400);
  }

  const environmentIndex = segments.findIndex(
    (segment) => segment.toLowerCase() === "environments" || segment.toLowerCase() === "e",
  );
  const appsIndex = segments.findIndex(
    (segment, index) => index > environmentIndex && segment.toLowerCase() === "apps",
  );
  const editIndex = segments.findIndex(
    (segment, index) => index > appsIndex && segment.toLowerCase() === "edit",
  );
  const environmentId = environmentIndex >= 0 ? segments[environmentIndex + 1] : undefined;
  const pathAppId = appsIndex >= 0 ? segments[appsIndex + 1] : undefined;
  const legacyAppId =
    pathAppId &&
    appsIndex === environmentIndex + 2 &&
    editIndex === appsIndex + 2 &&
    GUID_PATTERN.test(pathAppId)
      ? pathAppId
      : undefined;

  let queryAppId: string | undefined;
  const rawQueryAppId = parsed.searchParams.get("app-id");
  if (rawQueryAppId) {
    let decoded = rawQueryAppId;
    try {
      // URLSearchParams performs the first decode. A few Designer links carry a
      // second-encoded provider path, so decode one remaining layer when needed.
      if (decoded.includes("%")) decoded = decodeURIComponent(decoded);
    } catch {
      throw new ApiError("studioUrl app-id query contains invalid URL encoding", 400);
    }
    if (!decoded.includes("\\") && !decoded.includes("\0")) {
      const appIdSegments = decoded.split("/").filter(Boolean);
      const candidate = appIdSegments.at(-1);
      if (candidate && GUID_PATTERN.test(candidate)) queryAppId = candidate;
    }
  }

  if (legacyAppId && queryAppId && legacyAppId.toLowerCase() !== queryAppId.toLowerCase()) {
    throw new ApiError("studioUrl contains conflicting Canvas app IDs", 400);
  }
  const appId = queryAppId ?? legacyAppId;

  if (
    !environmentId ||
    !appId ||
    environmentIndex < 0 ||
    !ENVIRONMENT_ID_PATTERN.test(environmentId) ||
    !GUID_PATTERN.test(appId)
  ) {
    throw new ApiError(
      "studioUrl must be a Power Apps Designer URL containing a safe environment ID and app GUID",
      400,
    );
  }

  return { environmentId, appId, environmentCategory };
}

function normalizeChildResult(result: unknown): CanvasAuthoringHandlerResult {
  if (typeof result === "string") return result;
  if (result === null || result === undefined) return "";
  if (typeof result !== "object") return JSON.stringify(result) ?? "";

  const record = result as Record<string, unknown>;
  const content = Array.isArray(record.content) ? record.content : [];
  const text = content
    .filter(
      (item): item is { type: "text"; text: string } =>
        !!item &&
        typeof item === "object" &&
        (item as Record<string, unknown>).type === "text" &&
        typeof (item as Record<string, unknown>).text === "string",
    )
    .map((item) => item.text)
    .join("\n");

  if (record.isError === true) {
    throw new ApiError(text || "Canvas authoring MCP returned an error", 502);
  }

  const structured =
    record.structuredContent &&
    typeof record.structuredContent === "object" &&
    !Array.isArray(record.structuredContent)
      ? (record.structuredContent as Record<string, unknown>)
      : undefined;
  if (structured) {
    return { text: text || "Canvas authoring operation completed.", structured };
  }
  return text || "Canvas authoring operation completed.";
}

function validateCanvasSourceName(fileName: string): void {
  const stem = fileName.endsWith(".pa.yaml") ? fileName.slice(0, -".pa.yaml".length) : "";
  const windowsDeviceStem = stem.split(".", 1)[0]?.toUpperCase();
  const isWindowsDevice =
    windowsDeviceStem === "CON" ||
    windowsDeviceStem === "PRN" ||
    windowsDeviceStem === "AUX" ||
    windowsDeviceStem === "NUL" ||
    /^COM[1-9]$/.test(windowsDeviceStem ?? "") ||
    /^LPT[1-9]$/.test(windowsDeviceStem ?? "");
  if (
    basename(fileName) !== fileName ||
    fileName.includes("/") ||
    fileName.includes("\\") ||
    stem.endsWith(".") ||
    stem.endsWith(" ") ||
    isWindowsDevice ||
    !CANVAS_SOURCE_NAME_PATTERN.test(fileName)
  ) {
    throw new ApiError(
      "fileName must be a safe flat basename ending in .pa.yaml, such as App.pa.yaml or Screen1.pa.yaml",
      400,
    );
  }
}

function assertWellFormedUnicode(content: string): void {
  for (let index = 0; index < content.length; index += 1) {
    const current = content.charCodeAt(index);
    if (current >= 0xd800 && current <= 0xdbff) {
      const next = content.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) {
        throw new ApiError("content must be well-formed UTF-8 text", 400);
      }
      index += 1;
    } else if (current >= 0xdc00 && current <= 0xdfff) {
      throw new ApiError("content must be well-formed UTF-8 text", 400);
    }
  }
}

function sourceBytes(content: string): Buffer {
  assertWellFormedUnicode(content);
  if (content.includes("\0")) {
    throw new ApiError("Canvas source content must not contain NUL characters", 400);
  }
  const bytes = Buffer.from(content, "utf8");
  if (bytes.length > MAX_CANVAS_SOURCE_BYTES) {
    throw new ApiError("Canvas source file exceeds the 1 MiB safety limit", 413);
  }
  return bytes;
}

function decodeSource(bytes: Buffer): string {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    throw new ApiError("Canvas source file must contain valid UTF-8 text", 400);
  }
}

function sha256(bytes: Buffer): string {
  return createHash("sha256").update(bytes).digest("hex");
}

async function safeCanvasDirectory(directoryPath: string, writable = false): Promise<string> {
  if (
    directoryPath.includes("\0") ||
    !isAbsolute(directoryPath) ||
    resolve(directoryPath) !== directoryPath
  ) {
    throw new ApiError(
      "directoryPath must be an existing absolute, normalized path without traversal or a trailing separator",
      400,
    );
  }

  const root = parsePath(directoryPath).root;
  const resolvedHome = resolve(homedir());
  if (directoryPath === root || directoryPath === resolvedHome) {
    throw new ApiError(
      "directoryPath must not be a filesystem root or the user's home directory",
      400,
    );
  }

  const metadata = await lstat(directoryPath);
  if (metadata.isSymbolicLink() || !metadata.isDirectory()) {
    throw new ApiError("directoryPath must reference a real directory, not a symlink or file", 400);
  }
  const canonical = await realpath(directoryPath);
  const canonicalHome = await realpath(resolvedHome).catch(() => resolvedHome);
  if (canonical === parsePath(canonical).root || canonical === canonicalHome) {
    throw new ApiError(
      "directoryPath resolves to a filesystem root or the user's home directory",
      400,
    );
  }
  await access(canonical, writable ? fsConstants.R_OK | fsConstants.W_OK : fsConstants.R_OK);
  return canonical;
}

function canvasFilePath(directoryPath: string, fileName: string): string {
  validateCanvasSourceName(fileName);
  const path = join(directoryPath, fileName);
  if (dirname(path) !== directoryPath) {
    throw new ApiError("Canvas source file must remain inside directoryPath", 400);
  }
  return path;
}

async function readCanvasSource(
  directoryPath: string,
  fileName: string,
): Promise<CanvasSourceFile> {
  const filePath = canvasFilePath(directoryPath, fileName);
  const metadata = await lstat(filePath);
  if (metadata.isSymbolicLink() || !metadata.isFile()) {
    throw new ApiError("Canvas source path must reference a regular file, not a symlink", 400);
  }
  if (metadata.size > MAX_CANVAS_SOURCE_BYTES) {
    throw new ApiError("Canvas source file exceeds the 1 MiB safety limit", 413);
  }

  const noFollow = fsConstants.O_NOFOLLOW ?? 0;
  const handle = await open(filePath, fsConstants.O_RDONLY | noFollow);
  try {
    const opened = await handle.stat();
    if (!opened.isFile()) {
      throw new ApiError("Canvas source path must reference a regular file", 400);
    }
    if (opened.size > MAX_CANVAS_SOURCE_BYTES) {
      throw new ApiError("Canvas source file exceeds the 1 MiB safety limit", 413);
    }
    const bytes = await handle.readFile();
    if (bytes.length > MAX_CANVAS_SOURCE_BYTES) {
      throw new ApiError("Canvas source file exceeds the 1 MiB safety limit", 413);
    }
    return {
      fileName,
      sizeBytes: bytes.length,
      sha256: sha256(bytes),
      content: decodeSource(bytes),
      device: opened.dev,
      inode: opened.ino,
    };
  } finally {
    await handle.close();
  }
}

async function listCanvasSources(directoryPath: string): Promise<CanvasSourceFile[]> {
  const entries = await readdir(directoryPath, { withFileTypes: true });
  const sourceNames = entries
    .filter((entry) => entry.name.endsWith(".pa.yaml"))
    .sort((left, right) => left.name.localeCompare(right.name));
  if (sourceNames.length > MAX_CANVAS_SOURCE_FILES) {
    throw new ApiError(
      `Canvas source workspace exceeds the ${MAX_CANVAS_SOURCE_FILES}-file safety limit`,
      413,
    );
  }
  const files: CanvasSourceFile[] = [];
  let totalBytes = 0;

  for (const entry of sourceNames) {
    validateCanvasSourceName(entry.name);
    if (entry.isSymbolicLink() || !entry.isFile()) {
      throw new ApiError(
        `Canvas source ${entry.name} must be a regular file, not a symlink or directory`,
        400,
      );
    }
    const file = await readCanvasSource(directoryPath, entry.name);
    totalBytes += file.sizeBytes;
    if (totalBytes > MAX_CANVAS_WORKSPACE_BYTES) {
      throw new ApiError("Canvas source workspace exceeds the 32 MiB safety limit", 413);
    }
    files.push(file);
  }
  return files;
}

function assertExpectedSha256(file: CanvasSourceFile, expectedSha256: string | undefined): void {
  if (expectedSha256 && file.sha256 !== expectedSha256.toLowerCase()) {
    throw new ApiError(
      `Canvas source changed: expected SHA-256 ${expectedSha256.toLowerCase()}, current SHA-256 ${file.sha256}`,
      409,
    );
  }
}

async function writeAllAt(handle: Awaited<ReturnType<typeof open>>, bytes: Buffer): Promise<void> {
  let offset = 0;
  while (offset < bytes.length) {
    const result = await handle.write(bytes, offset, bytes.length - offset, offset);
    if (result.bytesWritten <= 0) {
      throw new ApiError("Unable to finish writing Canvas source file", 500);
    }
    offset += result.bytesWritten;
  }
}

async function removeTemporaryFile(filePath: string): Promise<void> {
  try {
    await unlink(filePath);
  } catch (error) {
    if (
      !(error instanceof Error) ||
      !("code" in error) ||
      (error as NodeJS.ErrnoException).code !== "ENOENT"
    ) {
      throw error;
    }
  }
}

export async function handleConnectCanvasAuthoring(
  context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringHandlerResult> {
  const parsed = connectSchema.parse(input);
  if (parsed.authFlow === "devicecode") {
    throw new ApiError(
      "devicecode authentication is not supported by this bridge because it requires MCP elicitation; use broker or browser",
      400,
    );
  }
  const target = parsed.studioUrl
    ? parseTrustedStudioUrl(parsed.studioUrl)
    : {
        environmentId: parsed.environmentId!,
        appId: parsed.appId!,
        environmentCategory: parsed.environmentCategory!,
      };
  const client = requireCanvasClient(context);
  const result = await client.connect({
    ...target,
    ...(parsed.authFlow ? { authFlow: parsed.authFlow } : {}),
    ...(parsed.loginHint ? { loginHint: parsed.loginHint } : {}),
    ...(parsed.tenantId ? { tenantId: parsed.tenantId } : {}),
    ...(parsed.forceAccountSelect !== undefined
      ? { forceAccountSelect: parsed.forceAccountSelect }
      : {}),
  });
  return normalizeChildResult(result);
}

export async function handleListCanvasControls(
  context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringHandlerResult> {
  emptySchema.parse(input);
  return normalizeChildResult(await requireCanvasClient(context).listControls());
}

export async function handleDescribeCanvasControl(
  context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringHandlerResult> {
  const parsed = describeControlSchema.parse(input);
  return normalizeChildResult(
    await requireCanvasClient(context).describeControl(parsed.controlName),
  );
}

export async function handleListCanvasApis(
  context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringHandlerResult> {
  emptySchema.parse(input);
  return normalizeChildResult(await requireCanvasClient(context).listApis());
}

export async function handleDescribeCanvasApi(
  context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringHandlerResult> {
  const parsed = describeApiSchema.parse(input);
  return normalizeChildResult(await requireCanvasClient(context).describeApi(parsed.apiName));
}

export async function handleListCanvasDataSources(
  context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringHandlerResult> {
  emptySchema.parse(input);
  return normalizeChildResult(await requireCanvasClient(context).listDataSources());
}

export async function handleGetCanvasDataSourceSchema(
  context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringHandlerResult> {
  const parsed = dataSourceSchema.parse(input);
  return normalizeChildResult(
    await requireCanvasClient(context).getDataSourceSchema(parsed.dataSourceName),
  );
}

export async function handleSyncCanvasSource(
  context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringHandlerResult> {
  const parsed = syncSchema.parse(input);
  if (!parsed.confirmOverwrite) {
    throw new ConfirmationRequiredError("Canvas source sync not confirmed. Set confirmOverwrite=true to permit overwriting local source files.");
  }
  const directoryPath = await safeCanvasDirectory(parsed.directoryPath, true);
  await listCanvasSources(directoryPath);
  const result = await requireCanvasClient(context).syncCanvas(directoryPath);
  await listCanvasSources(directoryPath);
  return normalizeChildResult(result);
}

export async function handleCompileCanvasSource(
  context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringHandlerResult> {
  const parsed = compileSchema.parse(input);
  if (!parsed.confirm) {
    throw new ConfirmationRequiredError("Canvas compilation not confirmed. Set confirm=true to modify the connected live app; compilation can mutate the app even when validation fails.");
  }
  const directoryPath = await safeCanvasDirectory(parsed.directoryPath);
  await listCanvasSources(directoryPath);
  return normalizeChildResult(await requireCanvasClient(context).compileCanvas(directoryPath));
}

export async function handleListCanvasSourceFiles(
  _context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringRichResult> {
  const parsed = directorySchema.parse(input);
  const directoryPath = await safeCanvasDirectory(parsed.directoryPath);
  const files = await listCanvasSources(directoryPath);
  const metadata = files.map(({ fileName, sizeBytes, sha256: digest }) => ({
    fileName,
    sizeBytes,
    sha256: digest,
  }));
  const text =
    metadata.length === 0
      ? `No Canvas .pa.yaml source files found in ${directoryPath}.`
      : [
          `Canvas source files in ${directoryPath} (${metadata.length}):`,
          ...metadata.map(
            (file) => `  ${file.fileName} — ${file.sizeBytes} bytes — SHA-256 ${file.sha256}`,
          ),
        ].join("\n");
  return { text, structured: { directoryPath, files: metadata } };
}

export async function handleReadCanvasSourceFile(
  _context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringRichResult> {
  const parsed = fileSchema.parse(input);
  const directoryPath = await safeCanvasDirectory(parsed.directoryPath);
  const file = await readCanvasSource(directoryPath, parsed.fileName);
  return {
    text: [
      `${file.fileName} — ${file.sizeBytes} bytes — SHA-256 ${file.sha256}`,
      "",
      file.content,
    ].join("\n"),
    structured: {
      directoryPath,
      fileName: file.fileName,
      sizeBytes: file.sizeBytes,
      sha256: file.sha256,
      content: file.content,
    },
  };
}

export async function handleWriteCanvasSourceFile(
  _context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringRichResult> {
  const parsed = writeSchema.parse(input);
  const bytes = sourceBytes(parsed.content);
  const directoryPath = await safeCanvasDirectory(parsed.directoryPath, true);
  const filePath = canvasFilePath(directoryPath, parsed.fileName);
  const noFollow = fsConstants.O_NOFOLLOW ?? 0;

  if (parsed.overwrite) {
    const existing = await readCanvasSource(directoryPath, parsed.fileName);
    assertExpectedSha256(existing, parsed.expectedSha256);

    // Write and fsync a 0600 file in the same directory, then atomically replace
    // the destination. This keeps a process crash from leaving truncated YAML.
    const temporaryPath = join(directoryPath, `.canvas-${randomUUID()}.tmp`);
    let temporaryCreated = false;
    let renamed = false;
    try {
      const temporaryHandle = await open(
        temporaryPath,
        fsConstants.O_WRONLY | fsConstants.O_CREAT | fsConstants.O_EXCL | noFollow,
        0o600,
      );
      temporaryCreated = true;
      try {
        await writeAllAt(temporaryHandle, bytes);
        await temporaryHandle.sync();
      } finally {
        await temporaryHandle.close();
      }

      const current = await readCanvasSource(directoryPath, parsed.fileName);
      if (
        current.device !== existing.device ||
        current.inode !== existing.inode ||
        current.sha256 !== existing.sha256
      ) {
        throw new ApiError(
          "Canvas source changed while preparing to overwrite it; retry after reading it",
          409,
        );
      }

      await rename(temporaryPath, filePath);
      renamed = true;
    } finally {
      if (temporaryCreated && !renamed) await removeTemporaryFile(temporaryPath);
    }
  } else {
    const handle = await open(
      filePath,
      fsConstants.O_WRONLY | fsConstants.O_CREAT | fsConstants.O_EXCL | noFollow,
      0o600,
    );
    try {
      await writeAllAt(handle, bytes);
      await handle.sync();
    } finally {
      await handle.close();
    }
  }

  const digest = sha256(bytes);
  return {
    text: `Wrote ${parsed.fileName} (${bytes.length} bytes, SHA-256 ${digest}).`,
    structured: {
      directoryPath,
      fileName: parsed.fileName,
      sizeBytes: bytes.length,
      sha256: digest,
      created: !parsed.overwrite,
    },
  };
}

export async function handleDeleteCanvasSourceFile(
  _context: CanvasAuthoringToolContext,
  input: unknown,
): Promise<CanvasAuthoringHandlerResult> {
  const parsed = deleteSchema.parse(input);
  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Canvas source deletion not confirmed. Set confirm=true to delete ${parsed.fileName}.`);
  }
  const directoryPath = await safeCanvasDirectory(parsed.directoryPath, true);
  const file = await readCanvasSource(directoryPath, parsed.fileName);
  assertExpectedSha256(file, parsed.expectedSha256);

  const filePath = canvasFilePath(directoryPath, parsed.fileName);
  const current = await readCanvasSource(directoryPath, parsed.fileName);
  if (
    current.device !== file.device ||
    current.inode !== file.inode ||
    current.sha256 !== file.sha256
  ) {
    throw new ApiError(
      "Canvas source changed while preparing to delete it; retry after reading it",
      409,
    );
  }
  await unlink(filePath);
  return `Deleted ${file.fileName} (${file.sizeBytes} bytes, SHA-256 ${file.sha256}).`;
}

export type CanvasAuthoringHandler = (
  context: CanvasAuthoringToolContext,
  input: unknown,
) => Promise<CanvasAuthoringHandlerResult>;

export const canvasAuthoringHandlers: Record<string, CanvasAuthoringHandler> = {
  connect_canvas_authoring: handleConnectCanvasAuthoring,
  list_canvas_controls: handleListCanvasControls,
  describe_canvas_control: handleDescribeCanvasControl,
  list_canvas_apis: handleListCanvasApis,
  describe_canvas_api: handleDescribeCanvasApi,
  list_canvas_data_sources: handleListCanvasDataSources,
  get_canvas_data_source_schema: handleGetCanvasDataSourceSchema,
  sync_canvas_source: handleSyncCanvasSource,
  compile_canvas_source: handleCompileCanvasSource,
  list_canvas_source_files: handleListCanvasSourceFiles,
  read_canvas_source_file: handleReadCanvasSourceFile,
  write_canvas_source_file: handleWriteCanvasSourceFile,
  delete_canvas_source_file: handleDeleteCanvasSourceFile,
};
